import { PERMISSIONS } from './permissions.js';
/**
 * System roles (ADR-019). Roles are DB rows; modules extend this list and the matrix below and
 * seed their rows in their own migration (Payroll: `PAYROLL_APPROVER`, `FINANCE`,
 * `VAT_TAX_OFFICER` — SRS §3.2 module table, ADR-052).
 */
export const ROLE_CODES = [
    'EMPLOYEE',
    'HR',
    'PAYROLL_APPROVER',
    'FINANCE',
    'VAT_TAX_OFFICER',
    'ADMIN',
];
export const RoleCode = {
    EMPLOYEE: 'EMPLOYEE',
    HR: 'HR',
    PAYROLL_APPROVER: 'PAYROLL_APPROVER',
    FINANCE: 'FINANCE',
    VAT_TAX_OFFICER: 'VAT_TAX_OFFICER',
    ADMIN: 'ADMIN',
};
export const ROLE_LABELS = {
    EMPLOYEE: 'Employee',
    HR: 'HR',
    PAYROLL_APPROVER: 'Payroll approver',
    FINANCE: 'Finance',
    VAT_TAX_OFFICER: 'VAT & Tax officer',
    ADMIN: 'Admin',
};
const SELF_PERMISSIONS = [
    'self.read',
    'self.update.personal',
    'self.update.contact',
    'self.update.emergency',
    'self.request.bank',
];
/** Time & Leave self-service (SRS §3.2 module table): every role, so every account. */
const TIME_SELF_PERMISSIONS = [
    'leave.request',
    'attendance.checkin',
    'attendance.regularise',
    'wfh.request',
];
const HR_PERMISSIONS = [
    ...SELF_PERMISSIONS,
    ...TIME_SELF_PERMISSIONS,
    'team.read',
    'employee.read.all',
    'employee.create',
    'employee.update.all',
    'salary.read.all',
    'sensitive.read.all',
    'salary.write',
    'bank.approve',
    'lookup.manage',
    'export.run',
    'account.invite',
    'payroll.read',
    'payroll.prepare',
    'leave.read.all',
    'leave.manage',
    'attendance.read.all',
    'attendance.manage',
    'calendar.manage',
];
/** Module roles (SRS §3.2 module table). Every account also holds EMPLOYEE for self-service. */
const PAYROLL_APPROVER_PERMISSIONS = [
    ...TIME_SELF_PERMISSIONS,
    'payroll.read',
    'payroll.approve',
];
const FINANCE_PERMISSIONS = [
    ...TIME_SELF_PERMISSIONS,
    'payroll.disburse',
];
const VAT_TAX_OFFICER_PERMISSIONS = [
    ...TIME_SELF_PERMISSIONS,
    'payroll.tax.manage',
];
/**
 * Role → permission matrix (SRS §3.2, ADR-007). The single place that maps roles to
 * permissions; covered by `roles.test.ts`.
 *
 * `team.read` is not granted to EMPLOYEE here: it is derived per user when they have reports
 * (see `resolvePermissions`).
 */
export const ROLE_PERMISSIONS = {
    EMPLOYEE: [...SELF_PERMISSIONS, ...TIME_SELF_PERMISSIONS],
    HR: HR_PERMISSIONS,
    PAYROLL_APPROVER: PAYROLL_APPROVER_PERMISSIONS,
    FINANCE: FINANCE_PERMISSIONS,
    VAT_TAX_OFFICER: VAT_TAX_OFFICER_PERMISSIONS,
    ADMIN: PERMISSIONS,
};
export function isRoleCode(value) {
    return ROLE_CODES.includes(value);
}
/**
 * Effective permissions = union of all roles held (SRS §3.2). Unknown role codes grant nothing,
 * so a role row added by a future module is harmless until its matrix entry ships.
 */
export function resolvePermissions(roles, options = {}) {
    const result = new Set();
    for (const role of roles) {
        if (!isRoleCode(role))
            continue;
        for (const permission of ROLE_PERMISSIONS[role])
            result.add(permission);
    }
    if (options.hasReports)
        result.add('team.read');
    return result;
}
//# sourceMappingURL=roles.js.map